// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { ReactNode } from 'react';
import {
  Image,
  Platform,
  Pressable,
  StyleSheet,
  View,
  type ImageStyle,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import { Text } from '@/components/Themed';
import GameWeightControl from '@/components/GameWeightControl';
import { getGameDisplayName } from '@/features/game-definitions/i18n';
import type { GameDefinition } from '@/features/game-definitions/types';
import { t } from '@/i18n';
import appColors from '@/theme/colors';
import { createGameImageResolver } from './gameImage';

/**
 * `editor` is the dense tile used inside the tour editor's fixed-column rows;
 * `round` is the roomier tile the single-round selection screen lays out with
 * percentage widths in a wrapping grid. They differ only in spacing/typography.
 */
export type GameTileVariant = 'editor' | 'round';

type Props = {
  game: GameDefinition;
  selected: boolean;
  /** Media page path used for game-artwork requests, e.g. `/tour/[tourId]/edit`. */
  mediaPagePath: string;
  variant?: GameTileVariant;
  locked?: boolean;
  lockedLabel?: string;
  onPress: () => void;
  onInfoPress: () => void;
  meritLadderLabel?: string;
  perTeam?: boolean;
  perAchievement?: boolean;
  perEvent?: boolean;
  weightPercent?: number;
  onWeightPress?: () => void;
  weightLocked?: boolean;
  /**
   * Caption naming the chosen rule set. Callers pass it only when the choice differs from the
   * definition's default — otherwise every tile grows a line that says nothing.
   */
  ruleSetLabel?: string;
  /** Fallback display name when neither i18n nor the definition provides one. */
  nameFallback?: string;
  /** Optional content rendered between the title and the artwork (e.g. a "recommended" ribbon). */
  badge?: ReactNode;
  /** Dims the tile — used for games that aren't recommended for the current field size. */
  deemphasised?: boolean;
  tileStyle?: StyleProp<ViewStyle>;
  imageStyle?: StyleProp<ImageStyle>;
};

export default function GameTile({
  game,
  selected,
  mediaPagePath,
  variant = 'editor',
  locked = false,
  lockedLabel,
  onPress,
  onInfoPress,
  meritLadderLabel,
  perTeam = false,
  perAchievement = false,
  perEvent = false,
  weightPercent,
  onWeightPress,
  weightLocked = false,
  ruleSetLabel,
  nameFallback,
  badge,
  deemphasised = false,
  tileStyle,
  imageStyle,
}: Props) {
  const variantStyles = variant === 'round' ? roundStyles : editorStyles;
  const imageSource = createGameImageResolver(mediaPagePath)(game.image, game.name);
  const displayName = getGameDisplayName(game, nameFallback);
  const showMerits = selected && !!meritLadderLabel && typeof weightPercent === 'number';

  return (
    // testIDs are derived from the game id rather than taken as a prop, so every screen using a
    // GameTile (round select-games and the tour-edit game editors) is instrumented identically.
    // Selection state is on accessibilityState, so a test asserts "is this game selected" through
    // the checkbox role instead of inspecting styles.
    <Pressable
      testID={`game-tile-${game.id}`}
      onPress={onPress}
      style={[
        styles.gameTile,
        variantStyles.gameTile,
        selected && styles.gameTileSelected,
        selected && variantStyles.gameTileSelected,
        deemphasised && styles.gameTileDeemphasised,
        locked && styles.gameTileLocked,
        tileStyle,
      ]}
      accessibilityRole="checkbox"
      // react-native-web 0.21 no longer maps accessibilityState to aria-*, so the tile was
      // rendering role="checkbox" with NO aria-checked — a checkbox that never tells a screen
      // reader whether it is ticked. aria-* props are supported on both platforms (RN 0.81),
      // and accessibilityState stays for older native paths.
      aria-checked={selected}
      aria-disabled={locked}
      accessibilityState={{ checked: selected, disabled: locked }}
    >
      <Text
        style={[styles.gameTileName, variantStyles.gameTileName]}
        numberOfLines={variant === 'editor' ? 1 : undefined}
      >
        {displayName}
      </Text>
      {badge}
      <View style={styles.gameImageWrap}>
        <Image
          source={imageSource}
          style={[styles.gameImage, variantStyles.gameImage, selected && styles.gameImageSelected, imageStyle]}
          resizeMode="cover"
        />
        {selected ? (
          <View style={styles.selectedCheckBadge}>
            <Text style={styles.selectedCheckText}>✓</Text>
          </View>
        ) : null}
        <Pressable
          testID={`game-tile-info-${game.id}`}
          style={styles.infoButton}
          onPress={onInfoPress}
          accessibilityRole="button"
          accessibilityLabel={t('selectGames.gameInfo')}
        >
          <Text style={styles.infoButtonText}>{t('selectGames.infoIcon')}</Text>
        </Pressable>
      </View>
      {showMerits ? (
        <View testID={`game-tile-merits-${game.id}`} style={styles.meritRow}>
          <GameWeightControl
            testID={`game-tile-weight-${game.id}`}
            weightPercent={weightPercent}
            onPress={onWeightPress ?? (() => {})}
            selected
            showLabel={false}
            size="compact"
            disabled={weightLocked || !onWeightPress}
            accessibilityLabel={t('selectGames.meritLadderA11y', {
              name: displayName,
              ladder: meritLadderLabel,
              weight: weightPercent,
            })}
          />
          <View style={styles.meritTextWrap}>
            <Text style={[styles.meritLadderText, variantStyles.meritLadderText]} numberOfLines={1}>
              {meritLadderLabel}
            </Text>
            <Text style={[styles.meritCaption, weightLocked && styles.meritCaptionLocked]} numberOfLines={1}>
              {weightLocked
                ? t('selectGames.meritWeightSetByTour')
                : perEvent
                  ? t('selectGames.meritsPerEventLabel')
                  : perAchievement
                    ? t('selectGames.meritsPerAchievementLabel')
                    : perTeam
                      ? t('selectGames.meritsPerTeamLabel')
                      : t('selectGames.meritsLabel')}
            </Text>
          </View>
        </View>
      ) : null}
      {selected && ruleSetLabel ? (
        <Text testID={`game-tile-rule-${game.id}`} style={styles.gameTileRuleLabel} numberOfLines={1}>
          {ruleSetLabel}
        </Text>
      ) : null}
      {locked && lockedLabel ? <Text style={styles.gameTileLockedLabel}>{lockedLabel}</Text> : null}
    </Pressable>
  );
}

const cardShadow = Platform.select({
  web: { boxShadow: '0px 3px 9px rgba(0,0,0,0.2)' },
  default: {
    shadowColor: '#C8C8B0',
    shadowOpacity: 0.2,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 3 },
  },
});

const styles = StyleSheet.create({
  gameTile: {
    backgroundColor: appColors.background.panelAlt,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    padding: 8,
    minHeight: 88,
    ...cardShadow,
  },
  gameTileSelected: {
    borderColor: appColors.border.accent,
  },
  gameTileDeemphasised: {
    opacity: 0.84,
    borderColor: appColors.border.default,
  },
  gameTileLocked: {
    borderColor: '#f59e0b',
    backgroundColor: '#251603',
  },
  gameImageWrap: {
    position: 'relative',
  },
  gameImage: {
    width: '100%',
    borderRadius: 10,
  },
  gameImageSelected: {
    opacity: 0.6,
  },
  infoButton: {
    position: 'absolute',
    top: 6,
    right: 6,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#F2FAF1',
    borderWidth: 1,
    borderColor: appColors.border.accent,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 3,
  },
  infoButtonText: {
    color: '#0B2E1A',
    fontSize: 15,
    fontWeight: '800',
  },
  selectedCheckBadge: {
    position: 'absolute',
    top: 6,
    left: 6,
    minWidth: 24,
    height: 24,
    borderRadius: 12,
    paddingHorizontal: 6,
    backgroundColor: '#16a34a',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 4,
  },
  selectedCheckText: {
    color: appColors.text.primary,
    fontSize: 14,
    fontWeight: '800',
    lineHeight: 14,
  },
  gameTileName: {
    fontWeight: '700',
    color: appColors.text.primary,
    textAlign: 'center',
  },
  gameTileRuleLabel: {
    marginTop: 4,
    fontSize: 11,
    color: '#C8C8B0',
    textAlign: 'center',
  },
  gameTileLockedLabel: {
    marginTop: 4,
    fontSize: 11,
    fontWeight: '700',
    color: '#fcd34d',
    textAlign: 'center',
  },
  meritRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginTop: 8,
  },
  meritTextWrap: {
    flexShrink: 1,
  },
  meritLadderText: {
    fontWeight: '700',
    color: appColors.text.primary,
  },
  meritCaption: {
    fontSize: 11,
    color: appColors.text.muted,
  },
  meritCaptionLocked: {
    color: '#fcd34d',
  },
});

const editorStyles = StyleSheet.create({
  gameTile: {
    flex: 1,
    overflow: 'hidden',
  },
  gameTileSelected: {
    borderWidth: 2,
  },
  gameTileName: {
    fontSize: 14,
    marginBottom: 8,
  },
  // Square artwork inside the editor's fixed-column rows.
  gameImage: {
    aspectRatio: 1,
  },
  // Small enough that a 4-step ladder ("6 / 3 / 1.5 / 0") fits untruncated.
  meritLadderText: {
    fontSize: 11,
  },
});

const roundStyles = StyleSheet.create({
  gameTile: {},
  gameTileSelected: {},
  gameTileName: {
    fontSize: 15,
    marginBottom: 6,
  },
  // Height is overridden per breakpoint by the screen's imageStyle.
  gameImage: {
    height: 140,
  },
  meritLadderText: {
    fontSize: 13,
  },
});
